// ABOUTME: Dialog to add a new country×business-line catalog configuration from the Country Configurations landing page (HCP-111556).
// ABOUTME: Reuses the catalog-config create mutation, which also auto-provisions the country-level (Root) scope on the backend.
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import {useSnackbar} from 'notistack'
import React, {useState} from 'react'
import {useHistory} from 'react-router-dom'

import {useTranslation} from '../../../../hooks/useTranslation'
import {useAllowedHConnectConfigCountries} from '../../HConnect/hooks/useAllowedHConnectConfigCountries'
import {
  useCountryConfigList,
  useCreateCountryConfig,
} from '../../CatalogConfigurations/hooks/useCatalogConfig'
import {
  CATALOG_BUSINESS_LINES,
  emptyBusinessLineConfig,
  type CatalogBusinessLine,
} from '../../CatalogConfigurations/types'

interface AddConfigurationDialogProps {
  open: boolean
  onClose: () => void
  defaultCountry?: string
  defaultBusinessLine?: string
}

const toDefaultBusinessLine = (value?: string): CatalogBusinessLine => {
  const upper = (value ?? '').toUpperCase()
  return CATALOG_BUSINESS_LINES.find((bl) => bl === upper) ?? 'CEM'
}

export const AddConfigurationDialog: React.FC<AddConfigurationDialogProps> = ({
  open,
  onClose,
  defaultCountry,
  defaultBusinessLine,
}) => {
  const {t} = useTranslation()
  const history = useHistory()
  const {enqueueSnackbar} = useSnackbar()

  const allowedCountries = useAllowedHConnectConfigCountries()
  const {data: existing, isError: isListError} = useCountryConfigList()

  const [country, setCountry] = useState((defaultCountry ?? '').toUpperCase())
  const [businessLine, setBusinessLine] = useState<CatalogBusinessLine>(
    toDefaultBusinessLine(defaultBusinessLine)
  )

  const countryCode = country.trim().toUpperCase()
  const create = useCreateCountryConfig(countryCode, businessLine)

  const isAllowedCountry = allowedCountries.some((c) => c.toUpperCase() === countryCode)
  const isDuplicate = (existing ?? []).some(
    (r) =>
      r.countryCode.toUpperCase() === countryCode &&
      r.businessLine.toUpperCase() === businessLine.toUpperCase()
  )
  // Block create until the existing list has loaded, so the duplicate check can't be bypassed.
  const canCreate =
    !create.isLoading &&
    existing !== undefined &&
    !isListError &&
    countryCode.length === 2 &&
    isAllowedCountry &&
    !isDuplicate

  const onCreate = () => {
    if (!canCreate) return
    create.mutate(emptyBusinessLineConfig(), {
      onSuccess: () => {
        enqueueSnackbar(
          t('countryConfigurations.addConfig.successMessage', {countryId: countryCode, businessLine}),
          {variant: 'success'}
        )
        onClose()
        history.push(`/catalog-configurations/${countryCode}/${businessLine}`)
      },
      onError: () => {
        enqueueSnackbar(t('countryConfigurations.addConfig.errorMessage'), {variant: 'error'})
      },
    })
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" data-cy="add-configuration-dialog">
      <DialogTitle>{t('countryConfigurations.addConfig.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{mb: 2}}>
          {t('countryConfigurations.addConfig.description')}
        </DialogContentText>
        <div style={{display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8}}>
          <FormControl fullWidth>
            <InputLabel id="add-config-country-label">
              {t('countryConfigurations.addConfig.countryLabel')}
            </InputLabel>
            <Select
              labelId="add-config-country-label"
              label={t('countryConfigurations.addConfig.countryLabel')}
              value={country}
              onChange={(e) => setCountry(String(e.target.value))}
              data-cy="add-config-country"
            >
              {allowedCountries.map((c) => (
                <MenuItem key={c} value={c.toUpperCase()}>
                  {c.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="add-config-bl-label">
              {t('countryConfigurations.addConfig.businessLineLabel')}
            </InputLabel>
            <Select
              labelId="add-config-bl-label"
              label={t('countryConfigurations.addConfig.businessLineLabel')}
              value={businessLine}
              onChange={(e) => setBusinessLine(e.target.value as CatalogBusinessLine)}
              data-cy="add-config-business-line"
            >
              {CATALOG_BUSINESS_LINES.map((bl) => (
                <MenuItem key={bl} value={bl}>
                  {bl}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {countryCode.length === 2 && !isAllowedCountry && (
            <Alert severity="warning" data-cy="add-config-not-allowed">
              {t('countryConfigurations.addConfig.notAllowedWarning', {countryId: countryCode})}
            </Alert>
          )}

          {isDuplicate && (
            <Alert severity="warning" data-cy="add-config-duplicate">
              {t('countryConfigurations.addConfig.duplicateWarning', {
                countryId: countryCode,
                businessLine,
              })}
            </Alert>
          )}

          {create.isError && (
            <Alert severity="error" data-cy="add-config-error">
              {t('countryConfigurations.addConfig.errorMessage')}
            </Alert>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-cy="add-config-cancel">
          {t('countryConfigurations.addConfig.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={onCreate}
          disabled={!canCreate}
          data-cy="add-config-submit"
        >
          {create.isLoading
            ? t('countryConfigurations.addConfig.creating')
            : t('countryConfigurations.addConfig.create')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
